let workerSrc = null;
/**
 * Point pdf.js at its worker script (a URL). Call once at startup, before any
 * PDF is opened. Lets an app pass e.g. Vite's `?url` import for the worker so
 * pdf.js itself never lands in the main bundle — this module already imports
 * the library lazily.
 */
export function setPdfWorkerSrc(src) {
    workerSrc = src;
}
/**
 * A 2D canvas that can hand back an image Blob, across environments:
 *  - `OffscreenCanvas` where it exists *and works* (Chromium, Firefox, Safari 16.4+)
 *  - an `HTMLCanvasElement` fallback (Playwright's WebKit, older Safari)
 * plus WebP → PNG fallback (Safari can't encode WebP from a canvas).
 */
let canvasKind = null;
function probeCanvas() {
    canvasKind ??= (async () => {
        let offscreen = false;
        if (typeof OffscreenCanvas !== 'undefined') {
            try {
                const b = await new OffscreenCanvas(1, 1).convertToBlob({ type: 'image/png' });
                offscreen = b.size > 0;
            }
            catch {
                offscreen = false;
            }
        }
        let webp = false;
        try {
            if (offscreen) {
                webp = (await new OffscreenCanvas(1, 1).convertToBlob({ type: 'image/webp' })).type === 'image/webp';
            }
            else if (typeof document !== 'undefined') {
                webp = document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp');
            }
        }
        catch {
            webp = false;
        }
        return { offscreen, type: webp ? 'image/webp' : 'image/png' };
    })();
    return canvasKind;
}
async function renderTarget(w, h) {
    const { offscreen, type } = await probeCanvas();
    if (offscreen) {
        const canvas = new OffscreenCanvas(w, h);
        const ctx = canvas.getContext('2d');
        if (!ctx)
            throw new Error('pdf: no 2d context');
        return { canvas, ctx, toBlob: () => canvas.convertToBlob({ type, quality: 0.9 }) };
    }
    if (typeof document === 'undefined')
        throw new Error('pdf: no canvas available to render a page');
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx)
        throw new Error('pdf: no 2d context');
    return {
        canvas,
        ctx,
        toBlob: () => new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('pdf: canvas.toBlob failed'))), type, 0.9)),
    };
}
let modPromise = null;
function pdfjs() {
    // the legacy build runs in Node and older browsers alike
    modPromise ??= import('pdfjs-dist/legacy/build/pdf.mjs').then((m) => {
        const mod = m;
        if (workerSrc)
            mod.GlobalWorkerOptions.workerSrc = workerSrc;
        return mod;
    });
    return modPromise;
}
export async function loadPdf(data) {
    const { getDocument, Util } = await pdfjs();
    const task = getDocument({ data, isEvalSupported: false });
    const doc = await task.promise;
    const outline = await buildOutline(doc);
    return {
        pageCount: doc.numPages,
        outline,
        async pageSize(n) {
            const v = (await doc.getPage(n)).getViewport({ scale: 1 });
            return { width: v.width, height: v.height };
        },
        async renderToBlob(n, o = {}) {
            const page = await doc.getPage(n);
            const base = page.getViewport({ scale: 1 });
            let scale = o.scale ?? (globalThis.devicePixelRatio || 1.5);
            if (o.maxDim)
                scale = Math.min(scale, o.maxDim / Math.max(base.width, base.height));
            const viewport = page.getViewport({ scale });
            const w = Math.ceil(viewport.width);
            const h = Math.ceil(viewport.height);
            const { canvas, ctx, toBlob } = await renderTarget(w, h);
            await page.render({
                canvasContext: ctx,
                viewport,
                canvas: canvas,
            }).promise;
            return toBlob();
        },
        async textContent(n) {
            const tc = await (await doc.getPage(n)).getTextContent();
            return tc.items
                .map((it) => ('str' in it ? it.str : ''))
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();
        },
        async textRects(n) {
            const page = await doc.getPage(n);
            const vp = page.getViewport({ scale: 1 });
            const tc = await page.getTextContent();
            const out = [];
            for (const it of tc.items) {
                if (!('str' in it) || it.str === '')
                    continue;
                // item transform → device space (y-down, origin top-left)
                const m = Util.transform(vp.transform, it.transform);
                const fontH = Math.hypot(m[2], m[3]) || it.height || 1;
                const x = m[4];
                const y = m[5] - fontH;
                out.push({
                    str: it.str,
                    x: x / vp.width,
                    y: y / vp.height,
                    w: it.width / vp.width,
                    h: fontH / vp.height,
                });
            }
            return out;
        },
        async destroy() {
            await task.destroy();
        },
    };
}
async function buildOutline(doc) {
    const raw = (await doc.getOutline());
    if (!raw)
        return [];
    const resolve = async (node) => {
        let href = '';
        try {
            const dest = typeof node.dest === 'string' ? await doc.getDestination(node.dest) : node.dest;
            const ref = Array.isArray(dest) ? dest[0] : null;
            if (ref)
                href = `#page=${(await doc.getPageIndex(ref)) + 1}`;
        }
        catch {
            /* unresolvable bookmark — leave href empty */
        }
        return {
            label: node.title,
            href,
            children: await Promise.all((node.items ?? []).map(resolve)),
        };
    };
    return Promise.all(raw.map(resolve));
}
//# sourceMappingURL=parse.js.map